import {utils} from '@wallaby-ui/core';
import {format} from 'date-fns';

import Filter, {FilterValueOptions} from './filter';
import {FilterUtils, ShortcutEnum} from 'src/js/lib/filtering';
import {FILTER_TYPE_DATE_RANGE} from 'src/js/lib/filtering/filters_definitions_constants';

class DateRangeFilter extends Filter {
  private shortcut?: ShortcutEnum | null = null;
  getShortcutFromValue(filterData){
    if (filterData.value.shortcut) {
      return filterData.value.shortcut;
    }
    if (Array.isArray(filterData.value) && filterData.value.length === 3) {
      return filterData.value[2];
    }
    return undefined;
  }

  constructor(filterData) {
    super(filterData);
    this.type = filterData.type || FILTER_TYPE_DATE_RANGE;
    if (filterData.value != null) {
      let fromValue = '*';
      let toValue = '*';
      if (filterData && filterData.value) {
        fromValue =
          filterData.value.startDate || filterData.value[0] || fromValue;
        toValue = filterData.value.endDate || filterData.value[1] || toValue;
      }
      this.value = {startDate: fromValue, endDate: toValue};
      this.shortcut = this.getShortcutFromValue(filterData);
    }
  }

  static fromString(serializedFilter) {
    if (serializedFilter.endsWith(':*')) {
      return new DateRangeFilter({
        property: serializedFilter.slice(0, -2),
        value: null,
      });
    }
    const {rangeFrom, rangeTo, property} =
      FilterUtils.getRangeFromString(serializedFilter);
    return new DateRangeFilter({
      property,
      value: {startDate: rangeFrom, endDate: rangeTo},
    });
  }

  static fromSchema(schemaNode) {
    return new DateRangeFilter({property: schemaNode.getName()});
  }

  getValue() {
    return [this.value.startDate, this.value.endDate];
  }

  getValueToString(options?: FilterValueOptions) {
    if (this.value != null) {
      const {startDate} = this.value;
      const {endDate} = this.value;
      if (options?.rangeProperty === 'START') {
        return `${startDate}`;
      }
      if (options?.rangeProperty === 'END') {
        return `${endDate}`;
      }
      return `'${startDate}Z' TO '${endDate}Z'`;
    }
    return undefined;
  }

  getRangeProperties(filterSchema) {
    const node = filterSchema
      ? filterSchema.getFilterByName(this.property)
      : undefined;
    return node?.getRangeProperties();
  }

  getStartDate() {
    return new Date(this.value.startDate);
  }

  getEndDate() {
    return new Date(this.value.endDate);
  }

  getShortcut() {
    return this.shortcut;
  }

  clone() {
    return new DateRangeFilter({
      type: this.type,
      property: this.property,
      value: {...this.value, shortcut: this.shortcut},
      id: this.id,
    });
  }

  toString() {
    if (this.value != null) {
      const startDateFormatted = format(
        new Date(this.value.startDate),
        utils.DateUtils.ISO_DATE_TIME_FORMAT
      );
      const endDateFormatted = format(
        new Date(this.value.endDate),
        utils.DateUtils.ISO_DATE_TIME_FORMAT
      );
      return `${this.property}:'${startDateFormatted}Z' TO '${endDateFormatted}Z'`;
    }
    return `${this.property}:*`;
  }

  toComponentString(filterSchema, translateService) {
    const node = filterSchema
      ? filterSchema.getFilterByName(this.property)
      : undefined;
    let label = '';
    let startDateFormatted;
    let endDateFormatted;
    if (translateService) {
      if (node) {
        label = `${
          translateService.messages[node.getLabel()]
            ? translateService.formatMessage({id: node.getLabel()})
            : node.getLabel()
        }: `;
      }
      startDateFormatted = translateService.formatDate(this.value.startDate);
      endDateFormatted = translateService.formatDate(this.value.endDate);
    } else {
      if (node) {
        label = `${node.getLabel()}: `;
      }
      startDateFormatted = format(
        new Date(this.value.startDate),
        utils.DateUtils.DEFAULT_DATE_FORMAT
      );
      endDateFormatted = format(
        new Date(this.value.endDate),
        utils.DateUtils.DEFAULT_DATE_FORMAT
      );
    }
    return `${label}${startDateFormatted} - ${endDateFormatted}`;
  }
}

export default DateRangeFilter;
